import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowDownUp, History, Search, PackageOpen, TriangleAlert } from "lucide-react";

const EMPTY = { code: "", name: "", unit: "un", location: "", description: "", quantity: 0, min_stock: 0 };
const KIND_LABEL = { entrada: "Entrada", saida: "Saída", ajuste: "Ajuste" };
const isLow = (p) => (p.quantity || 0) <= (p.min_stock || 0);
const errMsg = (e) => formatErr(e.response?.data?.detail) || e.message;

function PartDialog({ part, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (part) { setForm({ ...EMPTY, ...part }); } }, [part]);
  const isNew = part && !part.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try {
      const body = { ...form, quantity: Number(form.quantity) || 0, min_stock: Number(form.min_stock) || 0 };
      if (isNew) {
        delete body._request_id;
        await api.post("/parts", body);
        if (part._request_id) await api.post(`/part-requests/${part._request_id}/resolve`);
      }
      else { delete body.quantity; await api.patch(`/parts/${part.id}`, body); }
      toast.success(isNew ? "Peça cadastrada" : "Peça atualizada");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!part} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="part-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Nova Peça" : "Editar Peça"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Código *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1.5 font-mono-tag" data-testid="part-code-input" /></div>
          <div><Label>Unidade</Label><Input value={form.unit} onChange={set("unit")} className="rounded-sm mt-1.5" placeholder="un, m, kg, L" data-testid="part-unit-input" /></div>
          <div className="col-span-2"><Label>Nome *</Label><Input value={form.name} onChange={set("name")} className="rounded-sm mt-1.5" data-testid="part-name-input" /></div>
          <div className="col-span-2"><Label>Localização</Label><Input value={form.location} onChange={set("location")} className="rounded-sm mt-1.5" placeholder="Ex: Prateleira A3" data-testid="part-location-input" /></div>
          {isNew && <div><Label>Saldo inicial</Label><Input type="number" min="0" step="any" value={form.quantity} onChange={set("quantity")} className="rounded-sm mt-1.5" data-testid="part-qty-input" /></div>}
          <div><Label>Estoque mínimo</Label><Input type="number" min="0" step="any" value={form.min_stock} onChange={set("min_stock")} className="rounded-sm mt-1.5" data-testid="part-min-input" /></div>
          <div className="col-span-2"><Label>Descrição</Label><Textarea value={form.description} onChange={set("description")} rows={2} className="rounded-sm mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancelar</Button>
          <Button onClick={save} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="part-save-btn">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ part, onClose, onSaved }) {
  const [kind, setKind] = useState("entrada");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  useEffect(() => { if (part) { setKind("entrada"); setQty(1); setNote(""); } }, [part]);
  const save = async () => {
    try {
      await api.post(`/parts/${part.id}/movements`, { kind, quantity: Number(qty), note });
      toast.success("Movimentação registrada");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!part} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="move-dialog">
        <DialogHeader><DialogTitle>Movimentar {part?.code} — saldo {part?.quantity} {part?.unit}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="rounded-sm" data-testid="move-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="entrada">Entrada (soma)</SelectItem>
              <SelectItem value="saida">Saída (subtrai)</SelectItem>
              <SelectItem value="ajuste">Ajuste (define saldo)</SelectItem>
            </SelectContent>
          </Select>
          <div><Label>{kind === "ajuste" ? "Novo saldo" : "Quantidade"}</Label><Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="rounded-sm mt-1.5" data-testid="move-qty" /></div>
          <div><Label>Observação</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm mt-1.5" placeholder="Ex: NF 1234, inventário" data-testid="move-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancelar</Button>
          <Button onClick={save} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="move-save-btn">Registrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovementsSheet({ part, onClose }) {
  const [items, setItems] = useState([]);
  useEffect(() => { if (part) { api.get(`/parts/${part.id}/movements`).then((r) => setItems(r.data)); } }, [part]);
  return (
    <Sheet open={!!part} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="movements-sheet">
        <SheetHeader><SheetTitle>Movimentações — {part?.code} {part?.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2">
          {items.length === 0 && <div className="text-sm text-muted-foreground">Sem movimentações.</div>}
          {items.map((m) => (
            <div key={m.id} className="border rounded-sm p-2 text-sm">
              <div className="flex justify-between"><b>{KIND_LABEL[m.kind]} {m.quantity}</b><span className="text-xs text-muted-foreground">{fmtDate(m.at)}</span></div>
              <div className="text-xs text-muted-foreground">Saldo após: {m.balance} · por {m.by}{m.note && ` · ${m.note}`}</div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DeletePart({ part, onDeleted }) {
  const remove = async () => {
    try { await api.delete(`/parts/${part.id}`); toast.success("Peça excluída"); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm h-8 text-red-600" data-testid={`delete-part-${part.code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir {part.name}?</AlertDialogTitle>
          <AlertDialogDescription>A peça sai do cadastro. As OS que já usaram esta peça mantêm o registro.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-part-${part.code}`}>Excluir</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PendingRequests({ items, canEdit, onRegister, onChange }) {
  if (!items.length) return null;
  const dismiss = async (r) => {
    try { await api.post(`/part-requests/${r.id}/resolve`); toast.success("Solicitação descartada"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="rounded-sm shadow-none border-amber-500/50" data-testid="part-requests">
      <div className="p-4 border-b flex items-center gap-2 font-heading font-bold text-amber-700 dark:text-amber-400">
        <TriangleAlert className="w-4 h-4" /> Peças usadas em OS sem cadastro ({items.length})
      </div>
      {items.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2 px-4 py-3 border-b last:border-0 text-sm" data-testid={`part-request-${r.id}`}>
          <span className="font-semibold flex-1 min-w-[160px]">{r.name}</span>
          <span className="text-xs text-muted-foreground">{r.quantity} {r.unit} · {r.os_label} · {r.requested_by} · {fmtDate(r.at)}</span>
          {canEdit && (
            <>
              <Button size="sm" onClick={() => onRegister(r)} className="rounded-sm h-8 bg-orange-500 hover:bg-orange-600 text-white" data-testid={`register-request-${r.id}`}>Cadastrar</Button>
              <Button size="sm" variant="outline" onClick={() => dismiss(r)} className="rounded-sm h-8" data-testid={`dismiss-request-${r.id}`}>Descartar</Button>
            </>
          )}
        </div>
      ))}
    </Card>
  );
}

export default function Warehouse() {
  const { user } = useAuth();
  const canEdit = ["supervisor", "tecnico", "almoxarife"].includes(user?.role);
  const canDelete = ["supervisor", "almoxarife"].includes(user?.role);
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [moving, setMoving] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [requests, setRequests] = useState([]);
  const load = useCallback(() => Promise.all([
    api.get("/parts").then((r) => setParts(r.data)),
    api.get("/part-requests").then((r) => setRequests(r.data)).catch(() => setRequests([])),
  ]), []);
  useEffect(() => { load(); }, [load]);

  const term = q.toLowerCase();
  const list = parts.filter((p) => (!lowOnly || isLow(p)) && `${p.code} ${p.name} ${p.location}`.toLowerCase().includes(term));
  const lowCount = parts.filter(isLow).length;

  return (
    <div className="space-y-6" data-testid="warehouse-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Almoxarifado</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Estoque de Peças</h1>
        </div>
        {canEdit && <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="new-part-btn"><Plus className="w-4 h-4" /> Nova Peça</Button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="rounded-sm shadow-none p-4"><div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Itens cadastrados</div><div className="font-heading font-black text-2xl" data-testid="parts-total">{parts.length}</div></Card>
        <button onClick={() => setLowOnly(!lowOnly)} className={`text-left border rounded-sm p-4 transition-colors ${lowOnly ? "border-red-500 bg-red-500/5" : "bg-card"}`} data-testid="low-stock-filter">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-red-600 flex items-center gap-1"><TriangleAlert className="w-3 h-3" /> Abaixo do mínimo</div>
          <div className="font-heading font-black text-2xl text-red-600" data-testid="parts-low-count">{lowCount}</div>
        </button>
      </div>
      <PendingRequests items={requests} canEdit={canEdit} onChange={load}
        onRegister={(r) => setEditing({ ...EMPTY, name: r.name, unit: r.unit || "un", _request_id: r.id })} />
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
        <Input placeholder="Buscar código, nome, local..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-sm" data-testid="parts-search" />
      </div>
      <Card className="rounded-sm shadow-none overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead><TableHead>Peça</TableHead><TableHead className="hidden md:table-cell">Local</TableHead>
              <TableHead className="text-right">Saldo</TableHead><TableHead className="text-right">Mínimo</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10"><PackageOpen className="w-6 h-6 mx-auto mb-2" />Nenhuma peça encontrada</TableCell></TableRow>}
            {list.map((p) => (
              <TableRow key={p.id} data-testid={`part-row-${p.code}`}>
                <TableCell className="font-mono-tag font-semibold text-orange-600">{p.code}</TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{p.location}</TableCell>
                <TableCell className="text-right">
                  <span className="font-semibold" data-testid={`part-qty-${p.code}`}>{p.quantity} {p.unit}</span>
                  {isLow(p) && <Badge variant="outline" className="ml-2 rounded-sm text-red-600 border-red-500/40 bg-red-500/10" data-testid={`part-low-${p.code}`}>Baixo</Badge>}
                </TableCell>
                <TableCell className="text-right text-sm">{p.min_stock}</TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setViewing(p)} className="rounded-sm h-8" data-testid={`history-part-${p.code}`}><History className="w-3.5 h-3.5" /></Button>
                  {canEdit && <Button size="sm" variant="outline" onClick={() => setMoving(p)} className="rounded-sm h-8" data-testid={`move-part-${p.code}`}><ArrowDownUp className="w-3.5 h-3.5" /></Button>}
                  {canEdit && <Button size="sm" variant="outline" onClick={() => setEditing(p)} className="rounded-sm h-8" data-testid={`edit-part-${p.code}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                  {canDelete && <DeletePart part={p} onDeleted={load} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <PartDialog part={editing} onClose={() => setEditing(null)} onSaved={load} />
      <MoveDialog part={moving} onClose={() => setMoving(null)} onSaved={load} />
      <MovementsSheet part={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
